import React from "react";
import { TeaserData } from "./teaser";
import { LinkBoxTeaserEntry, LinkListEntry } from "../helper/interfaces";
import { getImageUrlFormat1_1_600 } from "../helper/images";
import CustomImage from "./ui/Image";
import { useLoggedRouter } from "../helper/useLoggedRouter";
import { AppTheme, ContentType, LinkType } from "../helper/enums";
import TeaserMinimizedContainerNN from "./teaserMinimizedContainerNN";
import TeaserAdMarker from "./teaserAdMarker";
import { getLinkObjectFromTeaser } from "../helper/link";

export default function TeaserLinkBox(data: TeaserData) {
  const teaser = data.teaser as LinkBoxTeaserEntry;
  const [routerPush] = useLoggedRouter();

  const isNNTheme = process.env.NEXT_PUBLIC_APP_THEME === AppTheme.nn;
  const isNBTheme = process.env.NEXT_PUBLIC_APP_THEME === AppTheme.nb;
  const internalNavigate = async (e, articleId?: string, ressortId?: string) => {
    e.stopPropagation();
    e.preventDefault();

    if (data.navigate) {
      data.navigate(articleId ?? undefined, ressortId ?? undefined);
    } else if(articleId) {
      await routerPush(`/article/${encodeURIComponent(articleId)}`);
    } else if(ressortId) {
      await routerPush(`/ressort/${encodeURIComponent(ressortId)}`);
    } else {
      await routerPush(`/article/${encodeURIComponent(teaser.id)}`);
    }
  };

  function renderLinkContent(
    imageUrl: string,
    linkEntry: LinkListEntry,
    creationDate: string
  ) {
    return (
      <div>
        <div className="flex items-stretch">
          <div className={`linkbox-image  ${imageUrl ? "" : "fallback-image"}`}>
            {imageUrl ? (
              <>
                <CustomImage
                  src={imageUrl}
                  fill
                  alt={teaser?.title}
                  className="object-cover"
                />
                {isNNTheme && linkEntry.paid && !linkEntry.hidePlusIcon && (
                  <div className="linkbox-info">
                    <div className="linkbox-nn">NN+</div>
                  </div>
                )}
              </>
            ) : (
              <>
                <CustomImage
                  src={
                    isNNTheme || linkEntry.paid
                      ? "/images/fallback_img_nn.png"
                      : "/images/fallback_img_nordbayern.png"
                  }
                  fill
                  className="object-cover"
                  alt={teaser?.title}
                />

                {isNNTheme && linkEntry.paid && !linkEntry.hidePlusIcon && (
                  <div className="linkbox-info">
                    <div className="linkbox-nn">NN+</div>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="linkbox-info__wrapper">
            <div className="flex flex-col items-start gap-1.5 mb-1">
              {isNBTheme && linkEntry.paid && !linkEntry.hidePlusIcon && (
                <div className="linkbox-nn">NN+</div>
              )}
              {linkEntry.subline && (
                <span className="linkbox-time">
                  {linkEntry.subline}
                </span>
              )}
            </div>
            <TeaserAdMarker adIdentifier={linkEntry.adIdentifier} />
            {linkEntry.topline && (
              <div className="linkbox__url_headline">{linkEntry.topline}</div>
            )}

            <h3 className="linkbox__url_title">{linkEntry.title}</h3>
          </div>
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      {teaser?.linkList?.length > 0 && (
        <div className="linkbox">
          {isNNTheme && <div className="linkbox__icon-wrapper"></div>}
          {teaser?.topline && (
            <div className="linkbox_url_headline">{teaser?.topline}</div>
          )}
          {teaser?.title && (
            <div className="linkbox__headline">{teaser?.title}</div>
          )}
          <div className="linkbox__urls">
            {teaser?.linkList?.map((linkEntry) => {
              const isVideo =
                teaser.type == ContentType.Video ||
                teaser.type == ContentType.GlomexVideo ||
                teaser.type == ContentType.DailyMotionVideo;
              if (
                isNNTheme &&
                (linkEntry.type == ContentType.Podcast || isVideo)
              ) {
                return (
                  <TeaserMinimizedContainerNN
                    key={linkEntry.id}
                    teaser={linkEntry}
                    navigate={data.navigate}
                  />
                );
              }
              // else if() {

              // }
              else {
                const linkObject = getLinkObjectFromTeaser(linkEntry);
                const internalUrl = linkObject.internalLink;
                const externalUrl = linkObject.externalLink;
                const imageUrl =
                  linkEntry?.images?.length > 0
                    ? getImageUrlFormat1_1_600(linkEntry.images[0])
                    : null;

                return (
                  <div key={linkEntry.id} className="flex items-stretch">
                    {externalUrl && !internalUrl && (
                      <a
                        key={`link_${linkEntry.id}`}
                        data-name={`article-${linkEntry.id}`}
                        href={externalUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="linkbox__url linkbox__url_external mb-2 linkbox-item__nb"
                      >
                        {renderLinkContent(
                          imageUrl ?? '',
                          linkEntry,
                          teaser.creationDate
                        )}
                      </a>
                    )}
                    {internalUrl && linkObject.articleId && (
                      <a
                        id={`a-internal-article-${linkObject.articleId}`}
                        href={`/article/${linkObject.articleId}`}
                        key={`teaser-link-${linkObject.articleId}`}
                        data-name={`article-${linkObject.articleId}`}
                        onClick={(e) => {
                          internalNavigate(e, linkObject.articleId, undefined);
                        }}
                        className="linkbox__url linkbox-item__nb"
                      >
                        {renderLinkContent(
                          imageUrl ?? '',
                          linkEntry,
                          teaser.creationDate
                        )}
                      </a>
                    )}
                    {internalUrl && linkObject.ressortId && (
                      <a
                        id={`a-internal-ressort-${linkObject.ressortId}`}
                        href={`/ressort/${linkObject.ressortId}`}
                        key={`teaser-link-${linkObject.ressortId}`}
                        data-name={`ressort-${linkObject.ressortId}`}
                        onClick={(e) => {
                          internalNavigate(e, undefined, linkObject.ressortId);
                        }}
                        className="linkbox__url linkbox-item__nb"
                      >
                        {renderLinkContent(
                          imageUrl ?? '',
                          linkEntry,
                          teaser.creationDate
                        )}
                      </a>
                    )}
                  </div>
                );
              }
            })}
          </div>
        </div>
      )}
    </React.Fragment>
  );
}
